import { Box } from '@mui/material';
import { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SigninBg from 'src/assets/tailwind/signinBg.svg';
import { InfoContainer } from 'src/components/Signup/InfoContainer';
import { LanguageDropdown } from 'src/components/Signup/LanguageDropdown';
import { useSignUpSection } from 'src/components/Signup/signUpSection/useSignUpSection';
import { VerifyEmail } from 'src/components/Signup/signUpSection/verifyEmail';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Icon, IconProps } from 'src/components/ui/icon';
import { Input } from 'src/components/ui/input';
import { IS_CAPTCHA_ENABLED, POST_DOMAIN_PART, RECAPTCHA_SITE_KEY } from 'src/config';
import { LANDING_PAGE } from 'src/utils/constants';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export const SocialLoginButton = ({
  icon,
  text
}: {
  icon: Pick<IconProps, 'icon'>['icon'];
  text: string;
}) => (
  <button className="tw:border-border-secondary tw:hover:cursor-pointer tw:w-full tw:h-[40] tw:rounded-l tw:border tw:rounded-md tw:py-2 tw:flex tw:items-center tw:justify-center tw:gap-2">
    <Icon width={16} height={16} icon={icon} />
    {text}
  </button>
);

export const Divider = () => (
  <div className="tw:border-b tw:border-border-primary tw:w-[48%] tw:h-0" />
);
export const OrDivider = () => {
  const { t } = useTranslation();
  return (
    <div className="tw:flex tw:gap-5 tw:items-center">
      <Divider />
      <div>{t(T.or)}</div>
      <Divider />
    </div>
  );
};

export const SignUpSection = () => {
  const { t } = useTranslation();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const {
    email,
    emailError,
    isMarketingConsent,
    isVerifying,
    isLoading,
    handleEmail,
    setIsVerifying,
    resendVerificationEmail,
    sendVerifyEmail,
    setIsMarketingConsent,
    recaptchaToken,
    setRecaptchaToken
  } = useSignUpSection({
    onSendVerifyError: () => recaptchaRef.current?.reset()
  });
  const isButtonDisabled = !email || (IS_CAPTCHA_ENABLED && !recaptchaToken);
  const submitButtonText = isButtonDisabled ? t(T.enterEmail) : t(T.continue);

  const handleResendVerificationEmail = () => {
    resendVerificationEmail(isMarketingConsent);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    recaptchaRef.current?.reset();
    handleEmail(e);
  };

  return (
    <div
      className="tw:min-h-screen tw:w-full tw:bg-cover tw:bg-center tw:bg-no-repeat tw:relative tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-4 tw:px-4 sm:tw:py-8 sm:tw:px-6"
      style={{ backgroundImage: `url(${SigninBg})` }}
    >
      {/* Language - above form on small screens */}
      <div className="tw:absolute tw:top-4 tw:right-4 sm:tw:top-6 sm:tw:right-6">
        <LanguageDropdown />
      </div>

      {isVerifying ? (
        <VerifyEmail
          loading={isLoading}
          email={email}
          onResend={handleResendVerificationEmail}
          onChangeEmail={() => setIsVerifying(false)}
        />
      ) : (
        <div className="tw:bg-white tw:rounded-xl tw:shadow-lg tw:w-full tw:max-w-116 tw:p-4 sm:tw:p-6 md:tw:p-8 tw:bg-primary tw:h-auto tw:max-h-[90vh] tw:overflow-y-auto tw:mt-10 sm:tw:mt-0 ">
          <h2 className=" tw:font-['Lato'] tw:text-3xl tw:leading-8 sm:tw:text-2xl tw:font-semibold tw:text-center tw:mb-2">
            {t(T.welcome)}!
          </h2>
          <p className="tw:font-['Lato'] tw:text-[14px] tw:text-center tw:font-normal tw:text-text-secondary tw:mb-4 sm:tw:mb-6 sm:tw:text-base">
            {t(T.fillDetailMsg)}
          </p>

          {/* OAuth buttons */}
          <div className="tw:gap-4 sm:tw:gap-6 tw:flex tw:flex-col tw:items-center tw:w-full">
            {/* <div className="tw:space-y-3">
              <SocialLoginButton
                icon={GoogleLogo}
                text={t(T.continueWithGoogle)}
              />
              <SocialLoginButton
                icon={MicrosoftLogo}
                text={t(T.continueWithMicrosoft)}
              />
            </div>

            <OrDivider /> */}

            {/* Email */}
            <div className="tw:gap-3 sm:tw:gap-4 tw:flex tw:flex-col tw:w-full">
              <div>
                <label
                  htmlFor="email"
                  className="tw:font-['Lato'] tw:font-medium tw:block  tw:text-[14px] tw:text-text-secondary tw:mb-1"
                >
                  {t(T.emailAddress)}
                </label>
                <Input
                  value={email}
                  id="email"
                  type="email"
                  placeholder="name@intechww.com"
                  onChange={(e) => handleEmailChange(e)}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.preventDefault();
                      return;
                    }
                    if (e.key === 'Enter' && !isButtonDisabled && !isLoading) {
                      e.preventDefault();
                      sendVerifyEmail(isMarketingConsent);
                    }
                  }}
                  autoComplete="off"
                  disabled={isLoading}
                />
                {emailError && (
                  <p className="tw:text-text-error tw:text-[14px] tw:mt-1">
                    {emailError}
                  </p>
                )}
              </div>
              {IS_CAPTCHA_ENABLED && (
                <Box className="tw:flex tw:justify-center tw:transform tw:scale-90 sm:tw:scale-100 tw:origin-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token)}
                  />
                </Box>
              )}
              <Button
                className={`tw:w-full tw:rounded-l tw:py-2 tw:font-medium tw:hover:bg-blue-700 tw:transition tw:hover:cursor-pointer 
                ${
                  isButtonDisabled
                    ? 'tw:bg-secondary tw:text-text-disabled'
                    : 'tw:bg-brand tw:text-text-over-brand'
                }
              `}
                type="button"
                {...(!isButtonDisabled && {
                  onClick: () => {
                    sendVerifyEmail(isMarketingConsent);
                  }
                })}
                disabled={isLoading || isButtonDisabled}
                text={submitButtonText}
                loading={isLoading}
              />
              <p className="tw:font-normal tw:text-[14px] sm:tw:text-md tw:text-text-secondary tw:leading-relaxed sm:tw:leading-loose tw:flex tw:flex-col tw:gap-1 tw:text-center sm:tw:text-left">
                <span>
                  {t(T.alreadyHaveAccount)}
                  <Link
                    to={LANDING_PAGE}
                    className="tw:text-brand tw:font-medium tw:ml-1 hover:tw:opacity-90"
                  >
                    {t(T.signIn)}
                  </Link>
                </span>
                <span>{t(T.dedicatedLoginUrl)}</span>
                <span>
                  ({t(T.example)}
                  <span>
                    <span className="tw:text-brand tw:font-normal tw:text-[14px]">
                      {`https://example.${POST_DOMAIN_PART}`}
                    </span>
                  </span>
                  )
                </span>
              </p>

              <div className="tw:w-full tw:min-h-15">
                <label className="tw:flex tw:items-start tw:gap-2 tw:text-xs tw:text-gray-600 tw:mt-2 sm:tw:mt-4">
                  <Checkbox
                    checked={isMarketingConsent}
                    onClick={() => setIsMarketingConsent((prev) => !prev)}
                  />
                  <span className="sm:tw:text-[14px]  tw:text-text-secondary tw:font-normal tw:text-[14px]">
                    {t(T.marketingConsent)}
                  </span>
                </label>
              </div>
            </div>

            {/* Support */}

            <div className="tw:flex tw:w-full tw:mt-4 sm:tw:mt-8 tw:items-center tw:justify-center">
              <InfoContainer />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
